import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { cn } from '../../lib/utils';

export type TooltipProps = {
  content: React.ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  children: React.ReactNode;
  className?: string;
  delay?: number;
  onlyWhenTruncated?: boolean;
};

interface TooltipPos {
  top: number;
  left: number;
  transform: string;
}

const getPosition = (
  rect: DOMRect,
  placement: NonNullable<TooltipProps['placement']>,
): TooltipPos => {
  const gap = 8;
  switch (placement) {
    case 'top':
      return { top: rect.top - gap, left: rect.left + rect.width / 2, transform: 'translate(-50%, -100%)' };
    case 'bottom':
      return { top: rect.bottom + gap, left: rect.left + rect.width / 2, transform: 'translate(-50%, 0)' };
    case 'left':
      return { top: rect.top + rect.height / 2, left: rect.left - gap, transform: 'translate(-100%, -50%)' };
    case 'right':
      return { top: rect.top + rect.height / 2, left: rect.right + gap, transform: 'translate(0, -50%)' };
  }
};

const Tooltip = React.forwardRef<HTMLDivElement, TooltipProps>(
  ({ content, placement = 'top', children, className, delay = 200, onlyWhenTruncated = false }, ref) => {
    const [visible, setVisible] = useState(false);
    const [pos, setPos] = useState<TooltipPos | null>(null);
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const tooltipId = useRef(`tooltip-${Math.random().toString(36).slice(2)}`).current;

    const isTruncated = useCallback((): boolean => {
      const el = wrapperRef.current;
      if (!el) return false;
      // Check the wrapper itself or its first child element for overflow
      const target = (el.firstElementChild as HTMLElement) ?? el;
      return target.scrollWidth > target.clientWidth;
    }, []);

    const updatePos = useCallback(() => {
      const el = wrapperRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setPos(getPosition(rect, placement));
    }, [placement]);

    const handleMouseEnter = useCallback(() => {
      if (onlyWhenTruncated && !isTruncated()) return;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        updatePos();
        setVisible(true);
      }, delay);
    }, [delay, updatePos, onlyWhenTruncated, isTruncated]);

    const handleMouseLeave = useCallback(() => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setVisible(false);
    }, []);

    useEffect(() => {
      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      };
    }, []);

    const setRefs = useCallback(
      (node: HTMLDivElement | null) => {
        wrapperRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      },
      [ref],
    );

    return (
      <div
        ref={setRefs}
        className={cn('relative block min-w-0', className)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        aria-describedby={visible ? tooltipId : undefined}
      >
        {children}
        {visible && pos &&
          ReactDOM.createPortal(
            <div
              id={tooltipId}
              role="tooltip"
              style={{ position: 'fixed', top: pos.top, left: pos.left, transform: pos.transform }}
              className="z-tooltip px-2.5 py-1.5 rounded-lg bg-surface-elevated text-xs text-content shadow-lg border border-subtle whitespace-nowrap pointer-events-none"
            >
              {content}
            </div>,
            document.body,
          )}
      </div>
    );
  },
);

Tooltip.displayName = 'Tooltip';

export default Tooltip;
